import {
  COURSE_STATUS_DRAFT,
  COURSE_STATUS_HIDDEN,
  COURSE_STATUS_PUBLISHED,
} from '@/constant/QueryKeyName';
import { IMinimizedCourseItem } from '@/interfaces/course.interface';
import {
  ICoursePlaylist,
  TAvailableCourseForPlaylist,
  TCoursePlaylistDetail,
} from '@/interfaces/coursePlaylist.interface';
import { IEnterpriseProfileEachMemberData } from '@/interfaces/enterprise-profile/member_tab.interface';
import { QueryFunctionContext } from 'react-query';
import { FOLDERS_PER_ROW } from '../../constant/numericParams';
import { IPaginationResponse } from '../../interfaces/common.interface';
import { IUserJoinedGroupForCreatePostModal } from '../../interfaces/group.interface';
import { IBusinessProfileGroup } from '../../interfaces/profile.interface';
import {
  CourseStatusEnum,
  FolderStatusEnum,
  IFoldersSearchData,
} from '../../interfaces/training.interface';
import { tmsHTTPRequest } from '../../services/axios/httpRequest';

export const hardDeleteFolder = (id: string) => {
  return tmsHTTPRequest.delete(`/v1/folders/hard-delete/${id}`);
};

export const restoreFolder = (id: string, data = null) => {
  return tmsHTTPRequest.put(`/v1/folders/restore/${id}`, data);
};

export const softDeleteFolder = (id: number | string) =>
  tmsHTTPRequest.put(`/v1/folders/soft-delete/${id}`, { id });

export const getAllFolderInTrash = ({
  pageParam = 1,
  queryKey,
}: QueryFunctionContext<any[], any>) => {
  return tmsHTTPRequest.get(
    `/v1/folders/get-all-in-trash?PageNumber=${pageParam}&PageSize=${
      queryKey[1] || FOLDERS_PER_ROW
    }`
  );
};

export const postNewFolder = (data: {
  name: string;
  description?: string;
  parentId?: string;
  status: FolderStatusEnum;
}) => {
  return tmsHTTPRequest.post('/v1/folders/create', data);
};

export const getFolderDetails = (folderId: string) => {
  return tmsHTTPRequest.get(`/v1/folders/details/${folderId}`);
};
const postSearchFolder = (data: IFoldersSearchData) => {
  return tmsHTTPRequest.post('/v1/folders/search', {
    ...data,
    advancedSearch: { fields: ['Name'] },
  });
};

export const middlewarePostSearchFolder = ({
  pageParam = 1,
  queryKey,
}: QueryFunctionContext<any[], any>) => {
  return postSearchFolder({
    keyword: queryKey[1],
    place: queryKey[2],
    pageNumber: pageParam,
    pageSize: queryKey[3] || FOLDERS_PER_ROW,
  });
};

export const getFolderPlan = (folderId: string) => {
  return tmsHTTPRequest.get(`/v1/folders/${folderId}/plan`);
};

export const putFolderAvatar = (data: any, folderId: string) => {
  return tmsHTTPRequest.put(`/v1/folders/${folderId}/avatar`, data, {
    headers: {
      'content-type': 'multipart/form-data',
    },
  });
};

export const putFolderCover = (data: any, folderId: string) => {
  return tmsHTTPRequest.put(`/v1/folders/${folderId}/cover`, data, {
    headers: {
      'content-type': 'multipart/form-data',
    },
  });
};

export const getFolderHashtags = (folderId: string) => {
  return tmsHTTPRequest.get(`/v1/folders/${folderId}/hashtags`);
};

export const getAllUserJoinedFolderOnCreateModal = ({
  pageParam = 1,
  queryKey,
}: QueryFunctionContext<any[], any>) => {
  return tmsHTTPRequest.get<
    IPaginationResponse<IUserJoinedGroupForCreatePostModal>
  >(`/v1/folders?PageNumber=${pageParam}&PageSize=${queryKey[1] || 8}`);
};

export const getAllFolderHashtag = (folderId: string) => {
  return tmsHTTPRequest.get(`/v1/folders/${folderId}/all-hashtags`);
};

export const getFolderSearchList = (keyword: string) => {
  return ({ pageParam = 1, queryKey }: QueryFunctionContext<any[], any>) => {
    const data = {
      pageNumber: pageParam,
      pageSize: queryKey[1],
      keyword: keyword,
    };
    return tmsHTTPRequest.post(`/v1/folders/group/search`, data);
  };
};

export const getListUserRequestJoinGroup = ({
  pageParam: _pageParam = 1,
  queryKey,
}: QueryFunctionContext<string[], number>) => {
  return tmsHTTPRequest.get(`/v1/folders/${queryKey[1]}/join-request`); // cause get all so dont pass pageSize and pageNumber
};

export const putAcceptRequestJoinPrivateGroup = (
  folderId: string,
  requestId?: string
) => {
  return tmsHTTPRequest.put(
    `/v1/folders/${folderId}/join-request/accept${
      requestId ? `?requestId=${requestId}` : ''
    }`,
    null
  );
};

export const deleteDenyRequestJoinPrivateGroup = (
  folderId: string,
  requestId: string
) => {
  return tmsHTTPRequest.delete(
    `/v1/folders/${folderId}/join-request/${requestId}`
  );
};

export const getBusinessProfileGroup = ({
  queryKey,
  pageParam = 1,
}: QueryFunctionContext) => {
  return tmsHTTPRequest.get<IPaginationResponse<IBusinessProfileGroup>>(
    `/v1/folders/user/${queryKey[2]}?pageSize=${queryKey[1]}&pageNumber=${pageParam}`
  );
};

export const getAllMembersBelongToGroupOfEnterpriseUser = ({
  queryKey,
  pageParam = 1,
}: QueryFunctionContext) => {
  return tmsHTTPRequest.get<
    IPaginationResponse<IEnterpriseProfileEachMemberData>
  >(
    `/v1/folders/enterprise/${queryKey[1]}/members?pageSize=${
      queryKey[3]
    }&pageNumber=${pageParam}${queryKey[2] ? `&viewUserId=${queryKey[2]}` : ''}`
  );
};

// export const getAllCoursesDoccenForEachJoinedGroup = async ({
//   pageParam = 1,
//   queryKey,
// }: QueryFunctionContext) => {
//   const [, pageSize, courseType, query] = queryKey;

//   const queryParams = new URLSearchParams({
//     pageNumber: pageParam.toString(),
//     pageSize: (String(pageSize) || 4).toString(),
//   });

//   if (courseType) {
//     queryParams.append('type', (String(courseType) || `''`).toString());
//   }

//   if (query) {
//     queryParams.append('query', (String(query) || `''`).toString());
//   }

//   return tmsHTTPRequest.get<IPaginationResponse<IFolder>>(
//     `/v1/folders/all?${queryParams.toString()}`
//   );
// };

export const getCoursesInGroup = ({
  pageParam = 1,
  queryKey,
}: QueryFunctionContext<any[], number>) => {
  const courseStatusEnumMapping: { [key: string]: string } = {
    [COURSE_STATUS_PUBLISHED]: CourseStatusEnum.Published,
    [COURSE_STATUS_DRAFT]: CourseStatusEnum.Draft,
    [COURSE_STATUS_HIDDEN]: CourseStatusEnum.Hidden,
  };

  return tmsHTTPRequest.get<IPaginationResponse<IMinimizedCourseItem>>(
    `/v1/folders/${queryKey[1]}/courses?Status=${
      courseStatusEnumMapping[queryKey[0]]
    }&PageNumber=${pageParam}&PageSize=${queryKey[2] || 4}`
  );
};

export const getCoursePlaylists = async (
  folderId: string
): Promise<ICoursePlaylist[]> => {
  return tmsHTTPRequest.get(`/v1/folders/${folderId}/playlists`);
};

export const getDetailCoursePlaylist = async ({
  folderId,
  playlistId,
}: {
  folderId: string;
  playlistId: string;
}) => {
  return tmsHTTPRequest.get<TCoursePlaylistDetail>(
    `/v1/folders/${folderId}/playlists/${playlistId}`
  );
};

export const postCreatePlaylist = async ({
  groupId,
  name,
}: {
  groupId: string;
  name: string;
}) => tmsHTTPRequest.post(`/v1/folders/${groupId}/playlists`, { name });

export const deletePlaylist = async ({
  groupId,
  playlistId,
}: {
  groupId: string;
  playlistId: string;
}) => tmsHTTPRequest.delete(`/v1/folders/${groupId}/playlists/${playlistId}`);

export const putUpdatePlaylist = async ({
  groupId,
  playlistId,
  data,
}: {
  groupId: string;
  playlistId: string;
  data: {
    name?: string;
    courseIds?: string[];
  };
}) =>
  tmsHTTPRequest.put(`/v1/folders/${groupId}/playlists/${playlistId}`, data);

export const getAvailabelCoursesForPlaylist = async (groupId: string) =>
  tmsHTTPRequest.get<TAvailableCourseForPlaylist>(
    `/v1/folders/${groupId}/courses/available`
  );
